import * as React from 'react';
import { MouseEvent, ReactElement } from 'react';
import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material';
import ActionDelete from '@mui/icons-material/Delete';
import clsx from 'clsx';
import inflection from 'inflection';
import {
    useTranslate,
    Button,
    DeleteWithConfirmButtonProps,
    Confirm,
    useResourceContext,
    useRecordContext,
} from 'react-admin';

import { useDeleteBranchWithConfirmController } from './controllers';
import { TreeRecord } from './types';

export const DeleteBranchWithConfirmButton = <
    RecordType extends TreeRecord = TreeRecord
>(
    props: DeleteWithConfirmButtonProps<RecordType>
): ReactElement => {
    const {
        className,
        confirmTitle = 'ra.message.delete_title',
        confirmContent = 'ra.message.delete_content',
        icon = defaultIcon,
        label = 'ra.action.delete',
        mutationMode,
        onClick,
        record: recordProp,
        resource: resourceProp,
        redirect = 'list',
        ...rest
    } = props;
    const translate = useTranslate();
    const resource = useResourceContext(props);
    const record = useRecordContext<RecordType>(props);
    const {
        handleDelete,
        handleDialogClose,
        handleDialogOpen,
        isLoading,
        open,
    } = useDeleteBranchWithConfirmController<RecordType>({
        mutationMode,
        resource,
        record,
        redirect,
    });

    const handleClick = (event: MouseEvent) => {
        if (onClick) {
            onClick(event);
        }

        if (!event.isDefaultPrevented()) {
            handleDialogOpen(event);
        }
    };

    return (
        <Root className={className}>
            <Button
                onClick={handleClick}
                label={label}
                className={clsx(
                    'ra-delete-button',
                    DeleteWithConfirmButtonClasses.deleteButton
                )}
                key="button"
                {...rest}
            >
                {icon}
            </Button>
            <Confirm
                isOpen={open}
                loading={isLoading}
                title={confirmTitle}
                content={confirmContent}
                translateOptions={{
                    name: translate(`resources.${resource}.forcedCaseName`, {
                        smart_count: 1,
                        _: inflection.humanize(
                            translate(`resources.${resource}.name`, {
                                smart_count: 1,
                                _: inflection.singularize(resource),
                            }),
                            true
                        ),
                    }),
                    id: record.id,
                }}
                onConfirm={handleDelete}
                onClose={handleDialogClose}
            />
        </Root>
    );
};

const defaultIcon = <ActionDelete />;

const PREFIX = 'RaDeleteBranchWithConfirmButton';
export const DeleteWithConfirmButtonClasses = {
    deleteButton: `${PREFIX}-deleteButton`,
};

const Root = styled('div', { name: PREFIX })(({ theme }) => ({
    [`& .${DeleteWithConfirmButtonClasses.deleteButton}`]: {
        color: theme.palette.error.main,
        '&:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.12),
            // Reset on mouse devices
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
    },
}));

DeleteBranchWithConfirmButton.propTypes = {
    className: PropTypes.string,
    confirmTitle: PropTypes.string,
    confirmContent: PropTypes.string,
    label: PropTypes.string,
    record: PropTypes.any,
    redirect: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.func,
    ]),
    resource: PropTypes.string,
    icon: PropTypes.element,
};
